import { useEffect, useState } from "react";
import {
  AudiencesClient,
  PartiesClient,
  type AttributeDefinitionSummary,
  type PartySummary,
} from "../api/generated";
import { usePermissions } from "../auth/SessionContext";
import { Alert, Chip, Empty, Field, Panel, describeError, useAsync } from "../components/ui";

const parties = new PartiesClient("");
const audiences = new AudiencesClient("");

/**
 * Console module: <b>Parties &amp; targets</b>, with the per-tenant attribute schema behind it.
 *
 * This is the client-onboarding surface: a client's own vocabulary, defined as data. That is what
 * makes onboarding data entry rather than code (§4.9, §10).
 */
export function PartiesPage() {
  const { hasCapability } = usePermissions();
  const canWrite = hasCapability("parties:write");
  const canAdmin = hasCapability("parties:admin");

  const list = useAsync(() => parties.parties_List(undefined, undefined));
  const definitions = useAsync(() => parties.parties_ListAttributeDefinitions());
  const schema = useAsync(() => audiences.audiences_Schema());

  const [editing, setEditing] = useState<PartySummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function remove(key: string) {
    if (!window.confirm(`Delete party "${key}"?`)) return;
    setError(null);
    try {
      await parties.parties_Delete(key);
      await list.reload();
    } catch (e) {
      setError(describeError(e));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Parties &amp; targets</h1>
          <p className="sub">
            A party is anything addressable — a store, a person, a system. Targets are the channels
            it can be reached on; attributes are how audiences select it.
          </p>
        </div>
        {canWrite && (
          <button onClick={() => setEditing({ key: "", type: "store", name: "", channels: [], attributes: {} })}>
            Add party
          </button>
        )}
      </div>

      {error && <Alert kind="error">{error}</Alert>}
      {list.error && <Alert kind="error">{list.error}</Alert>}

      {canAdmin && definitions.data && (
        <AttributeSchema definitions={definitions.data} onChanged={() => { definitions.reload(); schema.reload(); }} />
      )}

      {editing && schema.data && definitions.data && (
        <PartyEditor
          party={editing}
          definitions={definitions.data}
          channels={schema.data.channels ?? []}
          partyTypes={schema.data.partyTypes ?? []}
          nodes={(schema.data.nodes ?? []).map((n) => n.key!)}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await list.reload(); }}
        />
      )}

      <Panel title={`Parties${list.data ? ` (${list.data.length})` : ""}`}>
        {list.loading ? <Empty>Loading…</Empty> : (list.data ?? []).length === 0 ? (
          <Empty>No parties yet. Add one, or import them through the API.</Empty>
        ) : (
          <table>
            <thead>
              <tr><th>Key</th><th>Name</th><th>Type</th><th>Targets</th><th>Attributes</th><th>Node</th><th></th></tr>
            </thead>
            <tbody>
              {list.data!.map((p) => (
                <tr key={p.key}>
                  <td className="mono">{p.key}</td>
                  <td>{p.name}</td>
                  <td>{p.type}</td>
                  <td>{p.channels?.map((c) => <Chip key={c}>{c}</Chip>)}</td>
                  <td>
                    {Object.entries(p.attributes ?? {}).map(([k, v]) => (
                      <Chip key={k}>{k}={(v as string[]).join("|")}</Chip>
                    ))}
                  </td>
                  <td className="mono muted">{p.nodeKey ?? "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {canWrite && <button className="ghost small" onClick={() => setEditing(p)}>Edit</button>}{" "}
                    {canAdmin && <button className="danger small" onClick={() => remove(p.key!)}>Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}

function AttributeSchema({ definitions, onChanged }: {
  definitions: AttributeDefinitionSummary[];
  onChanged: () => void;
}) {
  const [key, setKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [allowed, setAllowed] = useState("");
  const [multi, setMulti] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setError(null);
    try {
      const values = allowed.split(",").map((v) => v.trim()).filter(Boolean);
      await parties.parties_UpsertAttributeDefinition(key, {
        key,
        displayName: displayName || key,
        dataType: values.length > 0 ? "enum" : "string",
        cardinality: multi ? "multi" : "single",
        allowedValues: values.length > 0 ? values : undefined,
        isFilterable: true,
        source: "hub",
      });
      setKey(""); setDisplayName(""); setAllowed("");
      onChanged();
    } catch (e) {
      setError(describeError(e));
    }
  }

  return (
    <Panel title="Attribute schema — this tenant's own vocabulary">
      {error && <Alert kind="error">{error}</Alert>}

      <table style={{ marginBottom: 14 }}>
        <thead>
          <tr><th>Key</th><th>Name</th><th>Type</th><th>Cardinality</th><th>Allowed values</th><th>Source</th><th>Freshness</th></tr>
        </thead>
        <tbody>
          {definitions.map((d) => (
            <tr key={d.key}>
              <td className="mono">{d.key}</td>
              <td>{d.displayName}</td>
              <td>{d.dataType}</td>
              <td>{d.cardinality}</td>
              <td className="muted">{d.allowedValues?.join(", ") ?? "any"}</td>
              <td>
                {d.source === "synced"
                  ? <Chip kind="warn">synced from {d.sourceSystem ?? "ERP"}</Chip>
                  : <Chip>hub</Chip>}
              </td>
              <td>
                {d.source === "synced"
                  ? (d.isStale ? <Chip kind="crit">stale</Chip> : <Chip kind="ok">fresh</Chip>)
                  : <span className="muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="row">
        <Field label="Key"><input value={key} onChange={(e) => setKey(e.target.value)} placeholder="region" /></Field>
        <Field label="Display name"><input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Region" /></Field>
        <Field label="Allowed values (optional, comma separated)">
          <input value={allowed} onChange={(e) => setAllowed(e.target.value)} placeholder="on, qc, ab" />
        </Field>
        <div className="checkbox" style={{ paddingBottom: 8 }}>
          <input id="multi" type="checkbox" checked={multi} onChange={(e) => setMulti(e.target.checked)} />
          <label htmlFor="multi">multi-valued</label>
        </div>
        <button className="ghost" onClick={add} disabled={!key}>Add attribute</button>
      </div>

      <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
        A <strong>synced</strong> attribute is owned by an external system and is read-only here.
        Changes made in the source system arrive on the next sync.
      </p>
    </Panel>
  );
}

function PartyEditor({ party, definitions, channels, partyTypes, nodes, onClose, onSaved }: {
  party: PartySummary;
  definitions: AttributeDefinitionSummary[];
  channels: string[];
  partyTypes: string[];
  nodes: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [key, setKey] = useState(party.key ?? "");
  const [name, setName] = useState(party.name ?? "");
  const [type, setType] = useState(party.type ?? "store");
  const [nodeKey, setNodeKey] = useState(party.nodeKey ?? "");
  const [selectedChannels, setSelectedChannels] = useState<string[]>(party.channels ?? []);
  const [attributes, setAttributes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(party.attributes ?? {})) next[k] = (v as string[]).join(", ");
    setAttributes(next);
  }, [party]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, string[]> = {};
      for (const definition of definitions) {
        if (definition.source === "synced") continue; // read-only here, by design
        const raw = attributes[definition.key!] ?? "";
        const values = raw.split(",").map((v) => v.trim()).filter(Boolean);
        if (values.length > 0) payload[definition.key!] = values;
      }

      await parties.parties_Upsert(key, {
        key,
        type,
        name,
        nodeKey: nodeKey || undefined,
        channels: selectedChannels,
        attributes: payload,
      });

      onSaved();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={party.key ? `Edit ${party.key}` : "New party"}
      actions={<button className="ghost small" onClick={onClose}>Cancel</button>}
    >
      {error && <Alert kind="error">{error}</Alert>}

      <div className="row">
        <Field label="Key">
          <input value={key} disabled={!!party.key} onChange={(e) => setKey(e.target.value)} placeholder="p_118" />
        </Field>
        <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Type">
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {partyTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        {nodes.length > 0 && (
          <Field label="Hierarchy node">
            <select value={nodeKey} onChange={(e) => setNodeKey(e.target.value)}>
              <option value="">(none)</option>
              {nodes.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <label>Targets — the systems this party can be reached on</label>
        <div style={{ display: "flex", gap: 14, marginTop: 4 }}>
          {channels.map((c) => (
            <div className="checkbox" key={c}>
              <input
                id={`pc-${c}`}
                type="checkbox"
                checked={selectedChannels.includes(c)}
                onChange={(e) =>
                  setSelectedChannels(e.target.checked
                    ? [...selectedChannels, c]
                    : selectedChannels.filter((x) => x !== c))
                }
              />
              <label htmlFor={`pc-${c}`}>{c}</label>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Attributes</label>
        <div className="grid-3">
          {definitions.map((d) => (
            <Field key={d.key} label={`${d.displayName}${d.source === "synced" ? " (synced — read only)" : ""}`}>
              <input
                disabled={d.source === "synced"}
                value={attributes[d.key!] ?? ""}
                onChange={(e) => setAttributes({ ...attributes, [d.key!]: e.target.value })}
                placeholder={d.allowedValues?.join(" | ") ?? (d.cardinality === "multi" ? "a, b" : "")}
              />
            </Field>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <button onClick={save} disabled={busy || !key || !name}>Save party</button>
      </div>
    </Panel>
  );
}
